import React, { memo } from 'react';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';

interface TablePaginationComponentProps {
    count: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}

const TablePaginationComponent: React.FC<TablePaginationComponentProps> = ({
    count,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
}) => {
    const handleFirstPage = () => {
        onPageChange(null, 0);
    };

    const handlePreviousPage = () => {
        if (page > 0) {
            onPageChange(null, page - 1);
        }
    };

    const handleNextPage = () => {
        if (page < Math.ceil(count / rowsPerPage) - 1) {
            onPageChange(null, page + 1);
        }
    };

    const handleLastPage = () => {
        const lastPage = Math.ceil(count / rowsPerPage) - 1;
        onPageChange(null, lastPage);
    };

    const ActionsComponent = memo(() => (
        <div className="flex items-center">
            <button
                onClick={handleFirstPage}
                disabled={page === 0}
                className={`p-2 ${page === 0 ? 'text-gray-400' : 'text-blue-600 hover:bg-blue-200'} rounded-full`}
                aria-label="First Page"
            >
                <FirstPageIcon />
            </button>
            <button
                onClick={handlePreviousPage}
                disabled={page === 0}
                className={`p-2 ${page === 0 ? 'text-gray-400' : 'text-blue-600 hover:bg-blue-200'} rounded-full`}
                aria-label="Previous Page"
            >
                <NavigateBeforeIcon />
            </button>

            {/* Page info displayed between buttons */}
            <span className="mx-2 text-sm text-gray-700">
                Page {page + 1} of {Math.ceil(count / rowsPerPage)}
            </span>

            <button
                onClick={handleNextPage}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                className={`p-2 ${page >= Math.ceil(count / rowsPerPage) - 1 ? 'text-gray-400' : 'text-blue-600 hover:bg-blue-200'} rounded-full`}
                aria-label="Next Page"
            >
                <NavigateNextIcon />
            </button>
            <button
                onClick={handleLastPage}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                className={`p-2 ${page >= Math.ceil(count / rowsPerPage) - 1 ? 'text-gray-400' : 'text-blue-600 hover:bg-blue-200'} rounded-full`}
                aria-label="Last Page"
            >
                <LastPageIcon />
            </button>
        </div>
    ));

    return (
        <div className="flex items-center justify-end p-4 bg-white border-t border-gray-200 w-full">
            <span className="text-sm text-gray-700 mr-4">
                Total Items: <strong>{count}</strong>
            </span>
            <div className="flex items-center">
                <span className="text-sm text-gray-700">Items Per Page:</span>
                <select
                    value={rowsPerPage}
                    onChange={onRowsPerPageChange}
                    className="ml-2 border rounded-md p-1"
                >
                    {[5, 10, 15, 20].map((option) => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>
            </div>
            {/* Display the total item count */}

            <ActionsComponent />
        </div>
    );
};

export default TablePaginationComponent;
